import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Panel } from "@/ui/Panel";
import { Button } from "@/ui/Button";
import { ErrorState } from "@/ui/States";
import { useToast } from "@/ui/Toast";
import { useService, serviceBase } from "@/features/services/useService";
import { VariablesPanel } from "@/features/services/VariablesPanel";
import { VolumesPanel } from "@/features/services/VolumesPanel";
import { ServiceSettingsPanel } from "@/features/services/ServiceSettingsPanel";

export const Route = createFileRoute(
  "/_app/o/$orgId/projects/$projectId/services/$serviceId/settings",
)({
  component: ServiceSettingsScreen,
});

function ServiceSettingsScreen() {
  const { orgId, projectId, serviceId } = Route.useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const service = useService(orgId, projectId, serviceId);

  const remove = useMutation({
    mutationFn: () =>
      api.delete<void>(serviceBase(orgId, projectId, serviceId)),
    onSuccess: async () => {
      toast.success("Service deleted");
      await queryClient.invalidateQueries({
        queryKey: ["services", orgId, projectId],
      });
      await navigate({
        to: "/o/$orgId/projects/$projectId",
        params: { orgId, projectId },
      });
    },
    onError: toast.error,
  });

  if (service.isError) {
    return (
      <ErrorState
        error={service.error}
        onRetry={() => void service.refetch()}
      />
    );
  }
  const svc = service.data?.service;
  if (!svc) return null;

  return (
    <div className="flex flex-col gap-6">
      <VariablesPanel
        orgId={orgId}
        projectId={projectId}
        serviceId={serviceId}
      />

      <VolumesPanel
        orgId={orgId}
        projectId={projectId}
        service={svc}
        onChanged={() =>
          queryClient.invalidateQueries({ queryKey: ["service", serviceId] })
        }
      />

      <ServiceSettingsPanel
        orgId={orgId}
        projectId={projectId}
        service={svc}
        onSaved={() =>
          queryClient.invalidateQueries({ queryKey: ["service", serviceId] })
        }
      />

      <Panel
        tone="down"
        title="Delete this service"
        description="Its configuration, variables and any attached storage are removed."
      >
        <Button
          tone="danger"
          size="dense"
          busy={remove.isPending}
          onClick={() => remove.mutate()}
        >
          Delete service
        </Button>
      </Panel>
    </div>
  );
}
